import Vue from "vue";
import posthog from 'posthog-js';
import { Component } from "vue-property-decorator";
import { INavbar } from "../../../../models/app-configuration";
import { Mutation, namespace, Getter, Action } from "vuex-class";
import { storeModules, storeTypes } from "../../../../store";
import { IAuthConfigModel } from "../../../../models/user/auth-config.model";
import EventTracker from "../../../../utils/event-tracker";
import { TRACKING_EVENTS } from "../../../../common/tracking-event.constants";
import EventBus from "../../../../utils/event-bus";
import { LOCALSTORAGE_AUTH2 } from "../../../../common/constants";
import { ConfigurationServices } from "../../../../services/configuration.service";

const CommonGetter = namespace(storeModules.Common, Getter);
const CommonMutation = namespace(storeModules.Common, Mutation);
const CommonAction = namespace(storeModules.Common, Action);
const UserGetter = namespace(storeModules.User, Getter);

// Add this to the navbars.json file after testing
const EMAIL_WEBSITE_INFO: any =   {
    "id": 20,
    "path": "/emails-website",
    "NameEN": "Emails (beta)",
    "NameNL": "Emails (beta)",
    "NameFR": "Emails (beta)",
    "icon": null,
    "index": 20,
    "parentId": 0,
    "navtiveId": 20,
    "isDefault": false,
    "name": "emails"
}

const KANBAN_INFO: any = {
    "id": 21,
    "path": "/kanban-website",
    "NameEN": "Kanban (beta)",
    "NameNL": "Kanban (beta)",
    "NameFR": "Kanban (beta)",
    "icon": null,
    "index": 21,
    "parentId": 0,
    "navtiveId": 21,
    "isDefault": false,
    "name": "kanban"
}

const DASHBOARD_INFO: any =   {
	"id": 10,
	"path": "/dashboard",
	"NameEN": "Dashboard",
	"NameNL": "Dashboard",
	"NameFR": "Dashboard",
	"icon": "icon-size-20 ico_dashboard",
	"index": 10,
	"parentId": 0,
	"navtiveId": 10,
	"isDefault": false,
	"name": "dashboard"
}

@Component({
	template: require("./template.html")
})
class NavbarComponent extends Vue {
	@CommonGetter
	navbar: INavbar;
	@CommonMutation
	setPageActive: (navbar: INavbar) => void;
	@CommonAction(storeTypes.Set.HORIZONTALPAGE)
	storeSetHorizontalPage: (isHorizontalPage: boolean) => boolean;

	@UserGetter('authConfig') private authConfig: IAuthConfigModel;

	dtNavbars: INavbar[];
	dtCurrentNavbar: INavbar | null;
	isCollapse: boolean = true;
    isShow: boolean = false;
    isDisplayButton: boolean = true;
    configServices: any = new ConfigurationServices();

	get coRouteName(): string {
		return this.$route.name;
	}
	get coCurrentNavbar(): INavbar {
		return this.navbar;
	}

	get dashboardModuleVisible() {
		return this.authConfig && this.authConfig.Config && this.authConfig.Config.ShowDashboardModule
	}

	get isSelectedEmails() {
		return this.$route?.path == '/emails-website'
	}

	get isSelectedKanban() {
		return this.$route?.path == '/kanban-website'
	}

	get customerName() {
		const authInfo = JSON.parse(localStorage.getItem(LOCALSTORAGE_AUTH2) || '{}');
		return authInfo?.data?.information?.customerName;
	}

	constructor() {
		super();
		this.dtNavbars = require("../../../../json/navbars.json") as INavbar[];
	}

	menuChange(m) {
        this.$router.push({ path: m.index });
        EventTracker.logEvent({
            FormName: TRACKING_EVENTS.PAGES.GLOBAL,
            Action: TRACKING_EVENTS.EVENTS.SELECT_MAIN_MENU,
            FormId: 0,
            Comment: {path: m.index}
        })
	}

	handleColapseMainMenu() {
		this.isCollapse = !this.isCollapse;
	}

	handleOpenMenu() {
		this.isShow = !this.isShow;
	}

	handleCloseMenu() {
		if (this.isShow) this.isShow = false;
    }

    async mounted() {
		// Handle collapse filter event
		EventBus.$on('COLLAPSE_FILTER', (val) => {
			this.isDisplayButton = !val;
		});
		
		// Handle sign document display status
		try {
			const systemConfigKeys = ['AllowEsignWithEidEasy'];
            const res = await this.configServices.getConfigValue(systemConfigKeys);
			if (!res?.SystemConfigs?.AllowEsignWithEidEasy) {
				this.dtNavbars = this.dtNavbars.filter(x => x.id !== 9);
			}
		} catch (error) {
			console.error('Error getting sign document display status:', error);
		}
		
		await this.$nextTick();
	
		// Handle Kanban feature flag - non-blocking approach
		this.checkPosthogFeatureFlag();
	}
    
    beforeDestroy() {
        EventBus.$off('COLLAPSE_FILTER');
    }

	async checkPosthogFeatureFlag() {
		// Check immediately first
		if (this.isKanbanFlagEnabled() && this.isEmailFlagEnabled() && this.isDashboardFlagEnabled()) {
			this.addKanbanToNavigation();
			this.addEmailToNavigation();
			this.addDashboardToNavigation();
			return;
		}
	
		// If not available immediately, set up periodic checks without blocking
		let retryCount = 0;
		const maxRetries = 5; // Reduced from 10
		const retryInterval = 1000; // Reduced from 2000ms
		
		const checkInterval = setInterval(() => {
			retryCount++;
			console.log(`Checking Kanban/email/dashboard feature flag - Attempt ${retryCount}`, this.isKanbanFlagEnabled(), this.isEmailFlagEnabled(), this.isDashboardFlagEnabled());

			if (this.isDashboardFlagEnabled()) {
				this.addDashboardToNavigation();
			}
			if (this.isKanbanFlagEnabled()) {
				this.addKanbanToNavigation();
			}
			if (this.isEmailFlagEnabled()) {
				this.addEmailToNavigation();
			}

			if (this.isKanbanFlagEnabled() && this.isEmailFlagEnabled()) {
				clearInterval(checkInterval);
			} else if (retryCount >= maxRetries) {
				console.log('Kanban/email/dashboard feature flag not available after maximum retries');
				clearInterval(checkInterval);
			}
		}, retryInterval);
	}
	
	addKanbanToNavigation() {
		// Check if already added to prevent duplicates
		if (!this.dtNavbars.find(nav => nav.id === KANBAN_INFO.id)) {
			this.dtNavbars.push(KANBAN_INFO);
		}
	}

	addEmailToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === EMAIL_WEBSITE_INFO.id)) {
			this.dtNavbars.push(EMAIL_WEBSITE_INFO);
		}
	}

	addDashboardToNavigation() {
		if (!this.dtNavbars.find(nav => nav.id === DASHBOARD_INFO.id)) {
			this.dtNavbars.push(DASHBOARD_INFO);
		}
	}
	
	isKanbanFlagEnabled() {
		return posthog.isFeatureEnabled('kanban-board');
	}

	isEmailFlagEnabled() {
		return posthog.isFeatureEnabled('automation-editor');
	}

	isDashboardFlagEnabled() {
		return posthog.isFeatureEnabled('dashboard-visible-flag');
	}
}

export default NavbarComponent;
